import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, Space, Typography, Skeleton, Modal, Button, Statistic } from "antd";
import { ArrowRightOutlined, CheckOutlined, CloseOutlined, ClockCircleOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import AppShell from "../../../components/layout/AppShell";
import Badge from "../../../components/ui/Badge";
import StatusAlert from "../../../components/ui/StatusAlert";
import { DetailCard } from "../../../components/ui/DetailsCard";
import {
    getOtcRequestById,
    respondOtcRequest,
    getInvoiceById,
    getBanks,
    createQuote,
    createTransaction,
} from "../../../services/api";
import { OTC_STATUS_OPTIONS } from "../../../utils/constants";
import { formatAmount, getMaxDecimalsForCurrency } from "../../../utils";

const { Text } = Typography;

const formatDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
        : "—";

const QUOTE_VALID_MS = 30 * 60 * 1000;

const OtcDetail = () => {
    const { otcRequestId } = useParams();
    const navigate = useNavigate();
    const userId = useSelector((s) => s.auth.userId);
    const quotePermissions = useSelector((s) => s.auth.quotePermissions);
    const isOnrampOfframp = quotePermissions?.canOnrampOfframp;
    const [loadingData, setLoadingData] = useState(false);
    const [otc, setOtc] = useState(null);
    const [responding, setResponding] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // "ACCEPTED" | "REJECTED" | null
    const [quoteExpired, setQuoteExpired] = useState(false);
    const quoteExpiresAt = otc?.updatedAt ? new Date(otc.updatedAt).getTime() + QUOTE_VALID_MS : null;
    const fetchOtcDetail = useCallback(() => {
        if (!otcRequestId) return;
        setLoadingData(true);
        getOtcRequestById(otcRequestId)
            .then((res) => setOtc(res?.data || null))
            .finally(() => setLoadingData(false));
    }, [otcRequestId]);

    useEffect(() => {
        fetchOtcDetail();
    }, [fetchOtcDetail]);

    useEffect(() => {
        const expired = quoteExpiresAt != null && quoteExpiresAt <= Date.now();
        setQuoteExpired(expired);
    }, [quoteExpiresAt, otc?.status]);


    const handleRespond = async (action) => {
        setResponding(true);
        try {
            await respondOtcRequest(otcRequestId, { status: action });

            const paymentRequestId = otc.paymentRequestId;

            if (action === OTC_STATUS_OPTIONS.REJECTED) {
                toast.success("OTC quote rejected!");
                navigate(`/sender/invoices/${paymentRequestId}/pay`);
                return;
            }

            const invRes = await getInvoiceById(paymentRequestId);
            const invoice = invRes?.data;
            if (!invoice?.fromUserId) {
                toast.error("Invoice not found for this OTC request.");
                return;
            }

            const banksRes = await getBanks(invoice.fromUserId);
            const approvedBank = (banksRes?.data?.banks ?? []).find((b) => b.status === "APPROVE");
            if (!approvedBank) {
                toast.error("Receiver bank account not found. Please ask the receiver to add a bank account.");
                return;
            }

            const quoteRes = await createQuote(
                isOnrampOfframp
                    ? {
                        userId,
                        fromCurrency: otc.fromCurrency,
                        toCurrency: "USD",
                        toAmount: String(invoice.amount),
                        category: "ONRAMP_OFFRAMP",
                        paymentRequestId,
                    }
                    : {
                        userId,
                        fromCurrency: "USDC",
                        toCurrency: otc.toCurrency,
                        toAmount: String(invoice.amount),
                        category: "OFFRAMP",
                        paymentRequestId,
                    }
            );
            const quoteId = quoteRes?.data?.id ?? quoteRes?.data?.quote_id;
            if (!quoteId) {
                toast.error("Failed to lock the OTC rate. Please try again.");
                return;
            }

            await createTransaction({ quoteId, paymentRequestId, bankAccountId: approvedBank.bankId });

            toast.success("OTC quote accepted!");
            navigate(`/sender/invoices/${paymentRequestId}/pay`);
        } catch (error) {
            const apiMessage = error?.response?.data?.message;
            toast.error(apiMessage || "Failed to respond. Please try again.");
            if (apiMessage === "OTC quote has expired — please create a new OTC request.") {
                const paymentRequestId = otc.paymentRequestId;
                navigate(`/sender/invoices/${paymentRequestId}/pay`);
            }
        } finally {
            setResponding(false);
            setConfirmAction(null);
        }
    };

    if (loadingData) {
        return (
            <AppShell>
                <Skeleton.Input active className="mb-2" />
                <Card title="Loading OTC Request Details" variant="borderless" style={{ width: "100%" }}>
                    <Skeleton active />
                </Card>
            </AppShell>
        );
    }

    if (!otc) {
        return (
            <AppShell>
                <p className="text-gray-500">OTC request not found.</p>
            </AppShell>
        );
    }

    const requestDetails = [
        {
            label: "REQUEST ID",
            value: (
                <Text strong className="text-sm text-slate-800 lg:whitespace-nowrap">
                    {otc.otcRequestId}
                </Text>
            ),
        },
        {
            label: "CURRENCY PAIR",
            value: (
                <Space className="rounded-lg bg-slate-50 px-3 py-2 border-collapse border border-slate-200">
                    <Text strong className="text-sm">{otc.fromCurrency}</Text>
                    <ArrowRightOutlined className="text-xs text-slate-400" />
                    <Text strong className="text-sm">{otc.toCurrency}</Text>
                </Space>
            ),
        },
        {
            label: "REQUESTED AMOUNT",
            value: (
                <Text strong className="text-base text-slate-800">
                    {otc.toCurrency} {formatAmount(otc.requestedAmount, getMaxDecimalsForCurrency(otc.toCurrency))}
                </Text>
            ),
        },
        {
            label: "REQUESTED RATE",
            value: (
                <Text strong className="text-base text-slate-800">
                    {formatAmount(otc.targetRate, getMaxDecimalsForCurrency(otc.fromCurrency))}
                </Text>
            ),
        },
        {
            label: "PAY AMOUNT",
            value: (
                <Text strong className="text-base text-slate-800">
                    {formatAmount(otc.payAmount, getMaxDecimalsForCurrency(otc.fromCurrency))}
                </Text>
            ),
        },
        {
            label: "OTC FINAL RATE",
            value: (
                <Text strong className="text-base text-slate-800 font-bold">
                    {formatAmount(otc.otcFinalRate, getMaxDecimalsForCurrency(otc.fromCurrency))}
                </Text>
            ),
        },
        {
            label: "LAST UPDATED",
            value: (
                <Text strong className="text-sm text-slate-800">
                    {formatDate(otc.updatedAt)}
                </Text>
            ),
        },
    ];

    return (
        <AppShell>
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3 mt-3">
                    <h1 className="text-lg font-semibold text-slate-900">OTC Request</h1>
                    <Badge status={otc.status || "INACTIVE"} />
                </div>
                <DetailCard title="Request Details" details={requestDetails} />
                <StatusAlert status={otc.status} />
                {(otc.status === "OTC_QUOTE_SHARED" && quoteExpired === false) && (
                    <Card title="Respond to Queue" variant="borderless" style={{ width: "100%" }}>
                        <div className="flex flex-col gap-3">
                            <p className="text-sm text-slate-500 m-0">
                                Accept to lock this rate and proceed to payment, or reject to decline the offer.
                            </p>
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    className="bg-green-600 hover:!bg-green-700 border-0"
                                    onClick={() => setConfirmAction(OTC_STATUS_OPTIONS.ACCEPTED)}
                                >
                                    Accept
                                </Button>
                                <Button
                                    danger
                                    icon={<CloseOutlined />}
                                    onClick={() => setConfirmAction(OTC_STATUS_OPTIONS.REJECTED)}
                                >
                                    Reject
                                </Button>
                            </Space>
                            {quoteExpiresAt && !quoteExpired && (
                                <div className="flex items-center gap-2">
                                    <ClockCircleOutlined className="text-slate-400" />
                                    <Text className="text-sm text-slate-500">
                                        This quote will expire in
                                    </Text>
                                    <Statistic.Countdown
                                        value={quoteExpiresAt}
                                        format="mm:ss"
                                        styles={{ content: { fontSize: 16, fontWeight: 600, color: "#dc2626" } }}
                                        onFinish={() => {
                                            handleRespond(OTC_STATUS_OPTIONS.REJECTED);
                                            return;
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </Card>
                )}
            </div>

            <Modal
                open={!!confirmAction}
                title={confirmAction === "ACCEPTED" ? "Accept OTC Quote" : "Reject OTC Quote"}
                okText={confirmAction === "ACCEPTED" ? "Accept" : "Reject"}
                okButtonProps={{ danger: confirmAction === "REJECTED", loading: responding }}
                onOk={() => handleRespond(confirmAction)}
                onCancel={() => setConfirmAction(null)}
                confirmLoading={responding}
            >
                <p className="text-sm text-slate-600">
                    {confirmAction === "ACCEPTED"
                        ? "Are you sure you want to accept this rate? This will lock the quote and generate a payment request."
                        : "Are you sure you want to reject this rate? This action cannot be undone."}
                </p>
            </Modal>
        </AppShell>
    );
};

export default OtcDetail;
